import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import "./BusinessApprovalDashboard.css";
import BusinessSubmissionDetails from "./BusinessSubmissionDetails";
import AdminConfigManager from "./AdminConfigManager";

const API_BASE = (
  process.env.REACT_APP_GOOURHUB_BUSINESS_API ||
  "https://business-api.goourhub.com"
).replace(/\/+$/, "");

const MAX_BUSINESS_PHOTOS = 4;
const MAX_BUSINESS_PHOTO_BYTES = 5 * 1024 * 1024;
const ALLOWED_BUSINESS_PHOTO_TYPES = new Set([
  "image/jpeg",
  "image/png",
  "image/webp",
]);

const STATUS_TABS = [
  "pending",
  "approved",
  "rejected",
];

const RECORD_TYPES = [
  "businesses",
  "events",
  "offers",
  "organizations",
];

const RECORD_TYPE_LABELS = {
  businesses: "Businesses",
  events: "Community Events",
  offers: "Offers & Deals",
  organizations: "Nonprofits",
};

const fmtTime = (value) => {
  if (!value) {
    return "";
  }

  const [hour, minute] = String(value)
    .split(":")
    .map(Number);

  if (!Number.isFinite(hour)) {
    return "";
  }

  const meridiem =
    hour >= 12
      ? "PM"
      : "AM";

  return `${hour % 12 || 12}:${String(
    minute || 0
  ).padStart(2, "0")} ${meridiem}`;
};

const eventTimeRange = (event) => {
  const start = fmtTime(
    event.start_time
  );

  if (!start) {
    return "";
  }

  const end = fmtTime(
    event.end_time
  );

  return end
    ? `${start} – ${end}`
    : start;
};

const formatDateTime = (value) => {
  if (!value) {
    return "";
  }

  const parsed = new Date(value);

  if (
    Number.isNaN(
      parsed.getTime()
    )
  ) {
    return String(value);
  }

  return parsed.toLocaleString();
};

const createInitialOfferForm = () => ({
  business_id: "",
  title: "",
  short_description: "",
  offer_text: "",
  promo_phrase: "",
  start_date: new Date().toISOString().slice(0, 10),
  end_date: "",
  conditions: "",
});

const createInitialOrganizationForm = () => ({
  slug: "",
  name: "",
  community: "",
  community_label: "",
  about: "",
  website: "",
  facebook: "",
  instagram: "",
  email: "",
  logo: "",
  volunteer: "",
  featured: false,
});

const organizationIdOf = (organization) => {
  const raw =
    organization?.id ||
    organization?._id ||
    organization?.organization_id ||
    "";

  if (typeof raw === "string") {
    return raw;
  }

  return (
    raw?.$oid ||
    raw?.hex ||
    String(raw || "")
  );
};

const organizationLabelOf = (organization) =>
  organization?.community_label ||
  organization?.communityLabel ||
  organization?.community ||
  "";

const organizationInitials = (name) =>
  String(name || "?")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");


function BusinessPhotoManager({
  business,
  uploadingId,
  onUploadPhotos,
}) {
  const photos = Array.isArray(
    business.photos
  )
    ? business.photos.filter(Boolean)
    : [];

  const coverPhoto =
    business.cover_photo ||
    business.coverPhoto ||
    photos[0] ||
    "";

  const remaining =
    MAX_BUSINESS_PHOTOS -
    photos.length;

  const isUploading =
    uploadingId === business.id;

  const inputId =
    `business-photo-upload-${business.id}`;

  return (
    <section className="business-admin-photos">
      <div className="business-admin-photos-head">
        <div>
          <strong>Business photos</strong>
          <span>
            {photos.length}/{MAX_BUSINESS_PHOTOS}
          </span>
        </div>

        <small>
          First photo is the cover.
        </small>
      </div>

      {photos.length > 0 ? (
        <div className="business-admin-photo-grid">
          {photos.map(
            (url, index) => (
              <a
                key={`${url}-${index}`}
                className="business-admin-photo"
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                title="Open full-size photo"
              >
                <img
                  src={url}
                  alt={`${business.name} photo ${index + 1}`}
                  loading="lazy"
                />

                {url === coverPhoto ||
                (!coverPhoto &&
                  index === 0) ? (
                  <span className="business-admin-photo-cover">
                    Cover
                  </span>
                ) : null}
              </a>
            )
          )}
        </div>
      ) : (
        <p className="business-admin-photo-empty">
          No photos added yet.
        </p>
      )}

      <div className="business-admin-photo-controls">
        <input
          id={inputId}
          className="business-admin-photo-input"
          type="file"
          accept="image/jpeg,image/png,image/webp"
          multiple
          disabled={
            isUploading ||
            remaining <= 0
          }
          onChange={(event) => {
            const files =
              Array.from(
                event.target.files || []
              );

            if (files.length) {
              onUploadPhotos(
                business,
                files
              );
            }

            event.target.value = "";
          }}
        />

        {remaining > 0 ? (
          <label
            className={`business-admin-photo-add${
              isUploading
                ? " is-disabled"
                : ""
            }`}
            htmlFor={inputId}
            aria-disabled={
              isUploading
            }
          >
            {isUploading
              ? "Uploading photos..."
              : photos.length
              ? `Add photos (${remaining} left)`
              : "Add photos"}
          </label>
        ) : (
          <span className="business-admin-photo-limit">
            4-photo limit reached
          </span>
        )}

        <small>
          JPG, PNG or WEBP · max
          5 MB each
        </small>
      </div>
    </section>
  );
}

function BusinessCard({
  business,
  savingId,
  photoUploadingId,
  onStatusChange,
  onDelete,
  onUploadPhotos,
  onCreateOffer,
}) {
  const isSaving =
    savingId === business.id;

  return (
    <article className="business-admin-card">
      <div className="business-admin-card-main">
        <span
          className={`business-status ${business.status}`}
        >
          {business.status}
        </span>

        <h2>{business.name}</h2>

        <BusinessSubmissionDetails
          business={business}
        />

        <BusinessPhotoManager
          business={business}
          uploadingId={photoUploadingId}
          onUploadPhotos={onUploadPhotos}
        />
      </div>

      <div className="business-admin-card-actions">
        {business.status === "approved" && (
          <button
            type="button"
            className="offer"
            disabled={isSaving}
            onClick={() => onCreateOffer(business)}
          >
            🏷️ Post offer / deal
          </button>
        )}

        {business.status !==
          "approved" && (
          <button
            type="button"
            className="approve"
            disabled={isSaving}
            onClick={() =>
              onStatusChange(
                business.id,
                "approved"
              )
            }
          >
            {isSaving
              ? "Saving..."
              : "Approve"}
          </button>
        )}

        {business.status !==
          "rejected" && (
          <button
            type="button"
            className="reject"
            disabled={isSaving}
            onClick={() =>
              onStatusChange(
                business.id,
                "rejected"
              )
            }
          >
            {isSaving
              ? "Working..."
              : "Reject"}
          </button>
        )}

        {business.status !==
          "pending" && (
          <button
            type="button"
            className="pending"
            disabled={isSaving}
            onClick={() =>
              onStatusChange(
                business.id,
                "pending"
              )
            }
          >
            Move to pending
          </button>
        )}

        <button
          type="button"
          className="delete"
          disabled={isSaving}
          onClick={() =>
            onDelete(business)
          }
        >
          {isSaving
            ? "Working..."
            : "Delete permanently"}
        </button>
      </div>
    </article>
  );
}


function OfferCreateModal({
  businesses,
  initialBusiness,
  loadingBusinesses,
  saving,
  onClose,
  onSubmit,
}) {
  const [form, setForm] = useState(() => ({
    ...createInitialOfferForm(),
    business_id: initialBusiness?.id || "",
  }));

  const [flyerFile, setFlyerFile] = useState(null);
  const [flyerPreview, setFlyerPreview] = useState("");

  useEffect(() => {
    return () => {
      if (flyerPreview) {
        URL.revokeObjectURL(flyerPreview);
      }
    };
  }, [flyerPreview]);

  useEffect(() => {
    if (initialBusiness?.id) {
      setForm((current) => ({
        ...current,
        business_id: initialBusiness.id,
      }));
    }
  }, [initialBusiness]);

  const selectedBusiness =
    businesses.find(
      (business) =>
        String(business.id) ===
        String(form.business_id)
    ) ||
    (initialBusiness?.id === form.business_id
      ? initialBusiness
      : null);

  const updateField = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const handleFlyerChange = (event) => {
    const file = event.target.files?.[0] || null;

    if (!file) {
      return;
    }

    const allowed = new Set([
      "image/jpeg",
      "image/png",
      "image/webp",
    ]);

    if (!allowed.has(file.type)) {
      window.alert("Offer flyer must be JPG, PNG, or WEBP.");
      event.target.value = "";
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      window.alert("Offer flyer must be 2 MB or smaller.");
      event.target.value = "";
      return;
    }

    if (flyerPreview) {
      URL.revokeObjectURL(flyerPreview);
    }

    setFlyerFile(file);
    setFlyerPreview(URL.createObjectURL(file));
    event.target.value = "";
  };

  const clearFlyer = () => {
    if (flyerPreview) {
      URL.revokeObjectURL(flyerPreview);
    }

    setFlyerFile(null);
    setFlyerPreview("");
  };

  const submit = (event) => {
    event.preventDefault();

    if (!selectedBusiness) {
      return;
    }

    onSubmit(
      {
        ...form,
        business_id: selectedBusiness.id,
        business_name: selectedBusiness.name,
        business_category:
          selectedBusiness.category || "business",
        image_url: "",
      },
      flyerFile
    );
  };

  return (
    <div
      className="business-admin-offer-modal-overlay"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <section
        className="business-admin-offer-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="business-admin-create-offer-title"
      >
        <div className="business-admin-offer-modal-head">
          <div>
            <span className="business-admin-kicker">
              Admin publish
            </span>
            <h2 id="business-admin-create-offer-title">
              Post offer / deal
            </h2>
            <p>
              Admin-created offers publish immediately.
              A business can have up to 3 active offers.
            </p>
          </div>

          <button
            type="button"
            className="business-admin-offer-close"
            onClick={onClose}
            aria-label="Close create offer"
          >
            ×
          </button>
        </div>

        <form
          className="business-admin-offer-form"
          onSubmit={submit}
        >
          <label>
            <span>Business *</span>
            <select
              name="business_id"
              value={form.business_id}
              onChange={updateField}
              required
              disabled={
                saving ||
                loadingBusinesses
              }
            >
              <option value="">
                {loadingBusinesses
                  ? "Loading approved businesses..."
                  : "Select approved business"}
              </option>

              {businesses.map((business) => (
                <option
                  key={business.id}
                  value={business.id}
                >
                  {business.name}
                  {business.category
                    ? ` — ${business.category}`
                    : ""}
                </option>
              ))}
            </select>
          </label>

          <label>
            <span>Offer title *</span>
            <input
              type="text"
              name="title"
              value={form.title}
              onChange={updateField}
              maxLength={120}
              placeholder="Example: 10% Off Your Meal"
              required
            />
          </label>

          <label>
            <span>Short description *</span>
            <textarea
              name="short_description"
              value={form.short_description}
              onChange={updateField}
              maxLength={300}
              rows={3}
              placeholder="A short explanation visitors will see."
              required
            />
          </label>

          <label>
            <span>Offer / deal *</span>
            <input
              type="text"
              name="offer_text"
              value={form.offer_text}
              onChange={updateField}
              maxLength={100}
              placeholder="Example: Get 10% off your total"
              required
            />
          </label>

          <label>
            <span>Promo code / phrase *</span>
            <input
              type="text"
              name="promo_phrase"
              value={form.promo_phrase}
              onChange={updateField}
              maxLength={100}
              placeholder="Example: Mention GoOurHub"
              required
            />
          </label>

          <div className="business-admin-offer-grid">
            <label>
              <span>Valid from *</span>
              <input
                type="date"
                name="start_date"
                value={form.start_date}
                onChange={updateField}
                required
              />
            </label>

            <label>
              <span>Valid until *</span>
              <input
                type="date"
                name="end_date"
                value={form.end_date}
                onChange={updateField}
                min={form.start_date}
                required
              />
            </label>
          </div>

          <label>
            <span>Conditions</span>
            <textarea
              name="conditions"
              value={form.conditions}
              onChange={updateField}
              maxLength={600}
              rows={3}
              placeholder="Example: Dine-in only. Cannot be combined with other offers."
            />
          </label>

          <div className="business-admin-offer-flyer">
            <div className="business-admin-offer-flyer-head">
              <div>
                <strong>
                  Offer flyer / image
                  <small> optional</small>
                </strong>
                <span>
                  Add one JPG, PNG or WEBP image · max 2 MB.
                </span>
              </div>
            </div>

            {flyerPreview ? (
              <div className="business-admin-offer-flyer-preview">
                <img
                  src={flyerPreview}
                  alt="Selected offer flyer preview"
                />
                <button
                  type="button"
                  onClick={clearFlyer}
                  disabled={saving}
                >
                  Remove
                </button>
              </div>
            ) : (
              <label className="business-admin-offer-flyer-add">
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handleFlyerChange}
                  disabled={saving}
                />
                <span aria-hidden="true">🖼️</span>
                <strong>Upload flyer</strong>
                <small>JPG, PNG or WEBP · max 2 MB</small>
              </label>
            )}
          </div>

          <div className="business-admin-offer-modal-actions">
            <button
              type="button"
              className="ghost"
              onClick={onClose}
              disabled={saving}
            >
              Cancel
            </button>

            <button
              type="submit"
              className="publish"
              disabled={
                saving ||
                !selectedBusiness
              }
            >
              {saving
                ? "Publishing..."
                : "Publish offer"}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}

function OfferCard({
  offer,
  savingId,
  flyerUploadingId,
  onDecision,
  onDelete,
  onUploadFlyer,
}) {
  const isSaving =
    savingId === offer.id;

  const isUploadingFlyer =
    flyerUploadingId === offer.id;

  const flyerInputId =
    `offer-flyer-${offer.id}`;

  return (
    <article className="business-admin-card">
      <div className="business-admin-card-main">
        <span
          className={`business-status ${offer.status}`}
        >
          {offer.status}
        </span>

        <span className="business-admin-community-chip">
          Offer / Deal
        </span>

        <h2>{offer.title}</h2>

        <p className="business-admin-meta">
          🏢{" "}
          <strong>
            Business:
          </strong>{" "}
          {offer.business_name}
        </p>

        {offer.business_category && (
          <p className="business-admin-meta">
            📂{" "}
            <strong>
              Category:
            </strong>{" "}
            {offer.business_category}
          </p>
        )}

        <p className="business-admin-meta">
          🏷️{" "}
          <strong>
            Offer:
          </strong>{" "}
          {offer.offer_text}
        </p>

        <p className="business-admin-meta">
          🔑{" "}
          <strong>
            Promo code / phrase:
          </strong>{" "}
          {offer.promo_phrase}
        </p>

        <p className="business-admin-meta">
          📅{" "}
          <strong>
            Valid:
          </strong>{" "}
          {offer.start_date} →{" "}
          {offer.end_date}
        </p>

        {offer.short_description && (
          <p>
            {offer.short_description}
          </p>
        )}

        {offer.conditions && (
          <p className="business-admin-meta">
            <strong>
              Conditions:
            </strong>{" "}
            {offer.conditions}
          </p>
        )}

        <section className="business-admin-existing-offer-flyer">
          <div className="business-admin-existing-offer-flyer-head">
            <strong>Offer flyer</strong>
            <span>
              {offer.image_url
                ? "Current flyer"
                : "No flyer uploaded yet"}
            </span>
          </div>

          {offer.image_url && (
            <div className="business-admin-existing-offer-flyer-image">
              <a
                href={offer.image_url}
                target="_blank"
                rel="noopener noreferrer"
                title="Open full-size flyer"
              >
                <img
                  src={offer.image_url}
                  alt={`${offer.title} flyer`}
                  loading="lazy"
                />
              </a>
            </div>
          )}

          <input
            id={flyerInputId}
            className="business-admin-existing-offer-flyer-input"
            type="file"
            accept="image/jpeg,image/png,image/webp"
            disabled={isUploadingFlyer || isSaving}
            onChange={(event) => {
              const file =
                event.target.files?.[0] || null;

              if (file) {
                onUploadFlyer(
                  offer,
                  file
                );
              }

              event.target.value = "";
            }}
          />

          <label
            htmlFor={flyerInputId}
            className={`business-admin-existing-offer-flyer-btn${
              isUploadingFlyer
                ? " is-disabled"
                : ""
            }`}
            aria-disabled={
              isUploadingFlyer ||
              isSaving
            }
          >
            {isUploadingFlyer
              ? "Uploading flyer..."
              : offer.image_url
              ? "🖼️ Replace flyer"
              : "🖼️ Upload flyer"}
          </label>

          <small className="business-admin-existing-offer-flyer-note">
            JPG, PNG or WEBP · max 2 MB
          </small>
        </section>

        <p className="business-admin-date">
          Submitted{" "}
          {formatDateTime(
            offer.created_at
          )}

          {offer.reviewed_at
            ? ` · Reviewed ${formatDateTime(
                offer.reviewed_at
              )}`
            : ""}
        </p>
      </div>

      <div className="business-admin-card-actions">
        {offer.status ===
          "pending" && (
          <>
            <button
              type="button"
              className="approve"
              disabled={isSaving}
              onClick={() =>
                onDecision(
                  offer,
                  "approved"
                )
              }
            >
              {isSaving
                ? "Publishing..."
                : "Approve & Publish"}
            </button>

            <button
              type="button"
              className="reject"
              disabled={isSaving}
              onClick={() =>
                onDecision(
                  offer,
                  "rejected"
                )
              }
            >
              {isSaving
                ? "Working..."
                : "Reject"}
            </button>
          </>
        )}

        <button
          type="button"
          className="delete"
          disabled={isSaving}
          onClick={() =>
            onDelete(offer)
          }
        >
          {isSaving
            ? "Working..."
            : "Delete permanently"}
        </button>
      </div>
    </article>
  );
}


function OrganizationForm({
  saving,
  onCancel,
  onSubmit,
}) {
  const [
    form,
    setForm,
  ] = useState(
    createInitialOrganizationForm
  );

  const updateField =
    (event) => {
      const {
        name,
        value,
        type,
        checked,
      } = event.target;

      setForm(
        (current) => ({
          ...current,
          [name]:
            type ===
            "checkbox"
              ? checked
              : value,
        })
      );
    };

  const submit =
    (event) => {
      event.preventDefault();

      const payload = {
        slug:
          form.slug
            .trim()
            .toLowerCase()
            .replace(
              /[^a-z0-9]+/g,
              "-"
            )
            .replace(
              /^-+|-+$/g,
              ""
            ),
        name:
          form.name.trim(),
        community:
          form.community
            .trim()
            .toLowerCase(),
        community_label:
          form.community_label.trim(),
        about:
          form.about.trim(),
        website:
          form.website.trim(),
        facebook:
          form.facebook.trim(),
        instagram:
          form.instagram.trim(),
        email:
          form.email.trim(),
        logo:
          form.logo.trim(),
        volunteer:
          form.volunteer.trim(),
        featured:
          Boolean(
            form.featured
          ),
      };

      if (
        !payload.slug ||
        !payload.name ||
        !payload.about
      ) {
        window.alert(
          "Slug, organization name, and about/mission are required."
        );
        return;
      }

      onSubmit(payload);
    };

  return (
    <section className="business-admin-org-form-wrap">
      <div className="business-admin-org-form-heading">
        <div>
          <span className="business-admin-kicker">
            Nonprofit / community org
          </span>
          <h2>Add organization</h2>
          <p>
            Add the organization once, then approve it when its
            information is ready to appear publicly.
          </p>
        </div>

        <button
          type="button"
          className="business-admin-org-form-close"
          onClick={onCancel}
          disabled={saving}
        >
          Close
        </button>
      </div>

      <form
        className="business-admin-org-form"
        onSubmit={submit}
      >
        <label className="business-admin-org-field">
          <span>Slug *</span>
          <input
            type="text"
            name="slug"
            value={form.slug}
            onChange={updateField}
            placeholder="example: sabcc"
            required
          />
          <small>
            Short unique ID used for links and organization records.
          </small>
        </label>

        <label className="business-admin-org-field">
          <span>Organization name *</span>
          <input
            type="text"
            name="name"
            value={form.name}
            onChange={updateField}
            placeholder="Organization name"
            required
          />
        </label>

        <label className="business-admin-org-field">
          <span>Community slug</span>
          <input
            type="text"
            name="community"
            value={form.community}
            onChange={updateField}
            placeholder="example: bengali"
          />
        </label>

        <label className="business-admin-org-field">
          <span>Community label</span>
          <input
            type="text"
            name="community_label"
            value={form.community_label}
            onChange={updateField}
            placeholder="Example: Bengali"
          />
        </label>

        <label className="business-admin-org-field business-admin-org-field--wide">
          <span>About / mission *</span>
          <textarea
            name="about"
            value={form.about}
            onChange={updateField}
            placeholder="One clear sentence about what the organization does."
            required
          />
        </label>

        <label className="business-admin-org-field">
          <span>Website</span>
          <input
            type="url"
            name="website"
            value={form.website}
            onChange={updateField}
            placeholder="https://..."
          />
        </label>

        <label className="business-admin-org-field">
          <span>Public email</span>
          <input
            type="email"
            name="email"
            value={form.email}
            onChange={updateField}
            placeholder="contact@example.org"
          />
        </label>

        <label className="business-admin-org-field">
          <span>Facebook</span>
          <input
            type="url"
            name="facebook"
            value={form.facebook}
            onChange={updateField}
            placeholder="https://facebook.com/..."
          />
        </label>

        <label className="business-admin-org-field">
          <span>Instagram</span>
          <input
            type="url"
            name="instagram"
            value={form.instagram}
            onChange={updateField}
            placeholder="https://instagram.com/..."
          />
        </label>

        <label className="business-admin-org-field">
          <span>Logo URL</span>
          <input
            type="url"
            name="logo"
            value={form.logo}
            onChange={updateField}
            placeholder="https://..."
          />
        </label>

        <label className="business-admin-org-field">
          <span>Volunteer link</span>
          <input
            type="url"
            name="volunteer"
            value={form.volunteer}
            onChange={updateField}
            placeholder="https://..."
          />
        </label>

        {form.logo && (
          <div className="business-admin-org-logo-preview">
            <img
              src={form.logo}
              alt=""
              onError={(event) => {
                event.currentTarget.style.display =
                  "none";
              }}
            />
            <span>
              Logo preview
            </span>
          </div>
        )}

        <label className="business-admin-org-check business-admin-org-field--wide">
          <input
            type="checkbox"
            name="featured"
            checked={form.featured}
            onChange={updateField}
          />
          <span>
            Feature / spotlight this organization
          </span>
        </label>

        <div className="business-admin-org-form-actions business-admin-org-field--wide">
          <button
            type="submit"
            className="business-admin-org-save"
            disabled={saving}
          >
            {saving
              ? "Saving..."
              : "Add organization"}
          </button>

          <button
            type="button"
            className="business-admin-org-cancel"
            onClick={onCancel}
            disabled={saving}
          >
            Cancel
          </button>
        </div>
      </form>
    </section>
  );
}

function OrganizationCard({
  organization,
  savingId,
  onStatusChange,
  onDelete,
}) {
  const id =
    organizationIdOf(
      organization
    );

  const isSaving =
    savingId === id;

  const logo =
    organization.logo ||
    organization.logo_url ||
    organization.logoUrl ||
    "";

  const label =
    organizationLabelOf(
      organization
    );

  const links = [
    [
      "Website",
      organization.website,
    ],
    [
      "Facebook",
      organization.facebook,
    ],
    [
      "Instagram",
      organization.instagram,
    ],
    [
      "Email",
      organization.email
        ? `mailto:${organization.email}`
        : "",
    ],
    [
      "Volunteer",
      organization.volunteer ||
        organization.volunteer_url ||
        organization.volunteerUrl,
    ],
    [
      "Donate",
      organization.donate ||
        organization.donate_url ||
        organization.donateUrl,
    ],
  ].filter(
    ([, href]) =>
      Boolean(href)
  );

  return (
    <article className="business-admin-card">
      <div className="business-admin-card-main">
        <div className="business-admin-org-head">
          <div className="business-admin-org-logo">
            {logo ? (
              <img
                src={logo}
                alt=""
                loading="lazy"
                onError={(event) => {
                  event.currentTarget.style.display =
                    "none";
                }}
              />
            ) : (
              organizationInitials(
                organization.name
              )
            )}
          </div>

          <div className="business-admin-org-title">
            <span
              className={`business-status ${
                organization.status ||
                "pending"
              }`}
            >
              {organization.status ||
                "pending"}
            </span>

            {organization.featured && (
              <span className="business-admin-org-featured">
                Featured
              </span>
            )}

            <h2>
              {organization.name}
            </h2>

            {(organization.slug ||
              id) && (
              <p className="business-admin-org-slug">
                {organization.slug ||
                  id}
              </p>
            )}
          </div>
        </div>

        {label && (
          <p className="business-admin-meta">
            🌍{" "}
            <strong>
              Community:
            </strong>{" "}
            {label}
          </p>
        )}

        {organization.about && (
          <p>
            {organization.about}
          </p>
        )}

        {links.length > 0 && (
          <div className="business-admin-org-links">
            {links.map(
              ([labelText, href]) => (
                <a
                  key={`${labelText}-${href}`}
                  href={href}
                  target={
                    href.startsWith(
                      "mailto:"
                    )
                      ? undefined
                      : "_blank"
                  }
                  rel={
                    href.startsWith(
                      "mailto:"
                    )
                      ? undefined
                      : "noopener noreferrer"
                  }
                >
                  {labelText}
                </a>
              )
            )}
          </div>
        )}

        <p className="business-admin-date">
          {organization.created_at ||
          organization.createdAt
            ? `Added ${formatDateTime(
                organization.created_at ||
                  organization.createdAt
              )}`
            : ""}
        </p>
      </div>

      <div className="business-admin-card-actions">
        {organization.status !==
          "approved" && (
          <button
            type="button"
            className="approve"
            disabled={isSaving}
            onClick={() =>
              onStatusChange(
                organization,
                "approved"
              )
            }
          >
            {isSaving
              ? "Saving..."
              : "Approve"}
          </button>
        )}

        {organization.status !==
          "rejected" && (
          <button
            type="button"
            className="reject"
            disabled={isSaving}
            onClick={() =>
              onStatusChange(
                organization,
                "rejected"
              )
            }
          >
            {isSaving
              ? "Working..."
              : "Reject"}
          </button>
        )}

        {organization.status !==
          "pending" && (
          <button
            type="button"
            className="pending"
            disabled={isSaving}
            onClick={() =>
              onStatusChange(
                organization,
                "pending"
              )
            }
          >
            Move to pending
          </button>
        )}

        <button
          type="button"
          className="delete"
          disabled={isSaving}
          onClick={() =>
            onDelete(
              organization
            )
          }
        >
          {isSaving
            ? "Working..."
            : "Delete permanently"}
        </button>
      </div>
    </article>
  );
}

function EventCard({
  event,
  savingId,
  onStatusChange,
  onDelete,
}) {
  const isSaving =
    savingId === event.id;

  return (
    <article className="business-admin-card">
      <div className="business-admin-card-main">
        <span
          className={`business-status ${event.status}`}
        >
          {event.status}
        </span>

        <span className="business-admin-community-chip">
          {event.community_slug}
        </span>

        <h2>{event.title}</h2>

        <p className="business-admin-meta">
          📅 {event.event_date}

          {eventTimeRange(event)
            ? ` · ${eventTimeRange(
                event
              )}`
            : ""}
        </p>

        {(event.venue_name ||
          event.address) && (
          <p className="business-admin-meta">
            📍{" "}
            {[
              event.venue_name,
              event.address,
            ]
              .filter(Boolean)
              .join(", ")}
          </p>
        )}

        {(event.organizer_name ||
          event.contact_email) && (
          <p className="business-admin-meta">
            👤 {event.organizer_name}

            {event.contact_email
              ? ` · ${event.contact_email}`
              : ""}
          </p>
        )}

        {event.ticket_url && (
          <p className="business-admin-meta">
            🎟️{" "}
            <a
              href={event.ticket_url}
              target="_blank"
              rel="noopener noreferrer"
            >
              {event.ticket_url}
            </a>
          </p>
        )}

        {event.description && (
          <p>{event.description}</p>
        )}

        {Array.isArray(
          event.tags
        ) &&
          event.tags.length >
            0 && (
            <div className="business-admin-tagrow">
              {event.tags.map(
                (tag) => (
                  <span key={tag}>
                    {tag}
                  </span>
                )
              )}
            </div>
          )}

        {Array.isArray(
          event.image_urls
        ) &&
          event.image_urls.length >
            0 && (
            <div className="business-admin-flyers">
              {event.image_urls.map(
                (
                  url,
                  index
                ) => (
                  <a
                    key={url}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img
                      src={url}
                      alt={`Flyer ${
                        index + 1
                      } for ${
                        event.title
                      }`}
                      loading="lazy"
                    />
                  </a>
                )
              )}
            </div>
          )}

        <p className="business-admin-date">
          Submitted{" "}
          {event.submitted_at}

          {event.reviewed_at
            ? ` · Reviewed ${event.reviewed_at}`
            : ""}
        </p>
      </div>

      <div className="business-admin-card-actions">
        {event.status !==
          "approved" && (
          <button
            type="button"
            className="approve"
            disabled={isSaving}
            onClick={() =>
              onStatusChange(
                event,
                "approved"
              )
            }
          >
            {isSaving
              ? "Saving..."
              : "Approve"}
          </button>
        )}

        {event.status !==
          "rejected" && (
          <button
            type="button"
            className="reject"
            disabled={isSaving}
            onClick={() =>
              onStatusChange(
                event,
                "rejected"
              )
            }
          >
            {isSaving
              ? "Working..."
              : "Reject"}
          </button>
        )}

        {event.status !==
          "pending" && (
          <button
            type="button"
            className="pending"
            disabled={isSaving}
            onClick={() =>
              onStatusChange(
                event,
                "pending"
              )
            }
          >
            Move to pending
          </button>
        )}

        <button
          type="button"
          className="delete"
          disabled={isSaving}
          onClick={() =>
            onDelete(event)
          }
        >
          {isSaving
            ? "Working..."
            : "Delete permanently"}
        </button>
      </div>
    </article>
  );
}

export default function BusinessApprovalDashboard() {
  const [
    adminKey,
    setAdminKey,
  ] = useState(
    () =>
      sessionStorage.getItem(
        "goourhubBusinessAdminKey"
      ) || ""
  );

  const [
    recordType,
    setRecordType,
  ] = useState("businesses");

  const [
    activeStatus,
    setActiveStatus,
  ] = useState("pending");

  const [
    records,
    setRecords,
  ] = useState([]);

  const [
    searchTerm,
    setSearchTerm,
  ] = useState("");

  const [
    loading,
    setLoading,
  ] = useState(false);

  const [
    savingId,
    setSavingId,
  ] = useState("");

  const [
    offerFlyerUploadingId,
    setOfferFlyerUploadingId,
  ] = useState("");

  const [
    photoUploadingId,
    setPhotoUploadingId,
  ] = useState("");

  const [
    offerModalOpen,
    setOfferModalOpen,
  ] = useState(false);

  const [
    offerTargetBusiness,
    setOfferTargetBusiness,
  ] = useState(null);

  const [
    offerBusinesses,
    setOfferBusinesses,
  ] = useState([]);

  const [
    offerBusinessesLoading,
    setOfferBusinessesLoading,
  ] = useState(false);

  const [
    offerSaving,
    setOfferSaving,
  ] = useState(false);

  const [
    organizationFormOpen,
    setOrganizationFormOpen,
  ] = useState(false);

  const [
    organizationSaving,
    setOrganizationSaving,
  ] = useState(false);

  const [
    message,
    setMessage,
  ] = useState("");

  const [
    error,
    setError,
  ] = useState("");

  const noun =
    recordType === "events"
      ? "event"
      : recordType === "offers"
      ? "offer"
      : recordType === "organizations"
      ? "organization"
      : "business";

  const pendingCount =
    useMemo(() => {
      if (
        activeStatus !==
        "pending"
      ) {
        return 0;
      }

      return records.filter(
        (item) =>
          item.status ===
          "pending"
      ).length;
    }, [
      activeStatus,
      records,
    ]);

  const filteredRecords =
    useMemo(() => {
      const query =
        searchTerm
          .trim()
          .toLowerCase();

      if (!query) {
        return records;
      }

      const tokens =
        query.split(/\s+/);

      return records.filter(
        (record) => {
          const haystack =
            JSON.stringify(
              record
            ).toLowerCase();

          return tokens.every(
            (token) =>
              haystack.includes(
                token
              )
          );
        }
      );
    }, [
      records,
      searchTerm,
    ]);

  const fetchRecords =
    async (
      status =
        activeStatus,
      keyOverride =
        adminKey,
      type =
        recordType
    ) => {
      const key =
        keyOverride.trim();

      if (!key) {
        setError(
          "Admin API key is required."
        );
        return;
      }

      setLoading(true);
      setError("");

      try {
        let endpoint;

        if (
          type === "events"
        ) {
          endpoint =
            `${API_BASE}/api/admin/events?status=${encodeURIComponent(
              status
            )}`;
        } else if (
          type === "offers"
        ) {
          endpoint =
            `${API_BASE}/api/offers/admin/${encodeURIComponent(
              status
            )}`;
        } else if (
          type === "organizations"
        ) {
          endpoint =
            `${API_BASE}/api/organizations/admin/${encodeURIComponent(
              status
            )}`;
        } else {
          endpoint =
            `${API_BASE}/api/admin/businesses?status=${encodeURIComponent(
              status
            )}`;
        }

        const response =
          await fetch(
            endpoint,
            {
              method: "GET",

              headers: {
                "X-Admin-API-Key":
                  key,
              },
            }
          );

        const data =
          await response
            .json()
            .catch(
              () => ({})
            );

        if (!response.ok) {
          throw new Error(
            data.message ||
              `Could not load ${type}.`
          );
        }

        if (
          type === "events"
        ) {
          setRecords(
            data.events || []
          );
        } else if (
          type === "offers"
        ) {
          setRecords(
            data.offers || []
          );
        } else if (
          type === "organizations"
        ) {
          setRecords(
            data.organizations ||
              data.items ||
              data.results ||
              []
          );
        } else {
          setRecords(
            data.businesses ||
              []
          );
        }
      } catch (err) {
        setError(
          err.message ||
            "Something went wrong."
        );

        setRecords([]);
      } finally {
        setLoading(false);
      }
    };

  const saveAdminKey =
    () => {
      const cleanKey =
        adminKey.trim();

      if (!cleanKey) {
        setError(
          "Paste your admin API key first."
        );
        return;
      }

      sessionStorage.setItem(
        "goourhubBusinessAdminKey",
        cleanKey
      );

      setMessage(
        "Admin key saved for this browser session."
      );

      setError("");

      fetchRecords(
        activeStatus,
        cleanKey,
        recordType
      );
    };

  const clearAdminKey =
    () => {
      sessionStorage.removeItem(
        "goourhubBusinessAdminKey"
      );

      setAdminKey("");
      setRecords([]);
      setMessage("");
      setError("");
    };

  const switchRecordType =
    (type) => {
      if (
        type === recordType
      ) {
        return;
      }

      setRecordType(type);
      setActiveStatus(
        "pending"
      );

      setRecords([]);
      setSearchTerm("");
      setMessage("");
      setError("");
    };

  const updateBusinessStatus =
    async (
      businessId,
      nextStatus
    ) => {
      const key =
        adminKey.trim();

      if (!key) {
        setError(
          "Admin API key is required."
        );
        return;
      }

      setSavingId(
        businessId
      );

      setError("");
      setMessage("");

      try {
        const response =
          await fetch(
            `${API_BASE}/api/admin/businesses/${encodeURIComponent(
              businessId
            )}/status`,
            {
              method:
                "PATCH",

              headers: {
                "Content-Type":
                  "application/json",

                "X-Admin-API-Key":
                  key,
              },

              body:
                JSON.stringify(
                  {
                    status:
                      nextStatus,
                  }
                ),
            }
          );

        const data =
          await response
            .json()
            .catch(
              () => ({})
            );

        if (!response.ok) {
          throw new Error(
            data.message ||
              "Could not update business."
          );
        }

        await fetchRecords(
          activeStatus,
          key,
          "businesses"
        );

        setMessage(
          `Business changed to ${nextStatus}.`
        );
      } catch (err) {
        setError(
          err.message ||
            "Something went wrong."
        );
      } finally {
        setSavingId("");
      }
    };

  const uploadBusinessPhotos =
    async (
      business,
      selectedFiles
    ) => {
      const key =
        adminKey.trim();

      if (!key) {
        setError(
          "Admin API key is required."
        );
        return;
      }

      const currentPhotos =
        Array.isArray(
          business.photos
        )
          ? business.photos.filter(
              Boolean
            )
          : [];

      const remaining =
        MAX_BUSINESS_PHOTOS -
        currentPhotos.length;

      if (remaining <= 0) {
        setError(
          `"${business.name}" already has 4 photos.`
        );
        return;
      }

      const files =
        Array.from(
          selectedFiles || []
        );

      if (!files.length) {
        return;
      }

      const invalidFile =
        files.find(
          (file) =>
            !ALLOWED_BUSINESS_PHOTO_TYPES.has(
              file.type
            )
        );

      if (invalidFile) {
        setError(
          `"${invalidFile.name}" is not supported. Use JPG, PNG, or WEBP.`
        );
        return;
      }

      const oversizedFile =
        files.find(
          (file) =>
            file.size >
            MAX_BUSINESS_PHOTO_BYTES
        );

      if (oversizedFile) {
        setError(
          `"${oversizedFile.name}" is larger than 5 MB.`
        );
        return;
      }

      const filesToUpload =
        files.slice(
          0,
          remaining
        );

      setPhotoUploadingId(
        business.id
      );
      setError("");
      setMessage("");

      let uploadedCount = 0;
      const failures = [];

      for (const file of filesToUpload) {
        try {
          const response =
            await fetch(
              `${API_BASE}/api/businesses/${encodeURIComponent(
                business.id
              )}/photos`,
              {
                method: "POST",
                headers: {
                  "Content-Type":
                    file.type,
                  "X-Admin-API-Key":
                    key,
                },
                body: file,
              }
            );

          const data =
            await response
              .json()
              .catch(
                () => ({})
              );

          if (!response.ok) {
            throw new Error(
              data.message ||
                `Photo upload failed (${response.status}).`
            );
          }

          uploadedCount += 1;
        } catch (err) {
          failures.push(
            `${file.name}: ${
              err.message ||
              "upload failed"
            }`
          );
        }
      }

      try {
        await fetchRecords(
          activeStatus,
          key,
          "businesses"
        );
      } finally {
        setPhotoUploadingId("");
      }

      const skippedCount =
        files.length -
        filesToUpload.length;

      if (uploadedCount > 0) {
        setMessage(
          `${uploadedCount} photo${
            uploadedCount === 1
              ? ""
              : "s"
          } added to "${business.name}".` +
            (skippedCount > 0
              ? ` ${skippedCount} extra photo${
                  skippedCount === 1
                    ? " was"
                    : "s were"
                } skipped because the maximum is 4.`
              : "")
        );
      }

      if (failures.length) {
        setError(
          `Some photos could not be uploaded: ${failures.join(
            " | "
          )}`
        );
      }
    };

  const loadApprovedBusinessesForOffer =
    async () => {
      const key =
        adminKey.trim();

      if (!key) {
        setError(
          "Admin API key is required."
        );
        return [];
      }

      setOfferBusinessesLoading(
        true
      );

      try {
        const response =
          await fetch(
            `${API_BASE}/api/admin/businesses?status=approved`,
            {
              method: "GET",
              headers: {
                "X-Admin-API-Key":
                  key,
              },
            }
          );

        const data =
          await response
            .json()
            .catch(
              () => ({})
            );

        if (!response.ok) {
          throw new Error(
            data.message ||
              "Could not load approved businesses."
          );
        }

        const businesses =
          Array.isArray(
            data.businesses
          )
            ? data.businesses
            : [];

        setOfferBusinesses(
          businesses
        );

        return businesses;
      } catch (err) {
        setError(
          err.message ||
            "Could not load approved businesses."
        );

        return [];
      } finally {
        setOfferBusinessesLoading(
          false
        );
      }
    };

  const openOfferCreator =
    async (
      business = null
    ) => {
      if (
        !adminKey.trim()
      ) {
        setError(
          "Admin API key is required."
        );
        return;
      }

      setError("");
      setMessage("");
      setOfferTargetBusiness(
        business
      );
      setOfferModalOpen(
        true
      );

      const loaded =
        await loadApprovedBusinessesForOffer();

      if (
        business &&
        !loaded.some(
          (item) =>
            String(
              item.id
            ) ===
            String(
              business.id
            )
        )
      ) {
        setOfferBusinesses(
          [
            business,
            ...loaded,
          ]
        );
      }
    };

  const createAdminOffer =
    async (
      offerData,
      flyerFile
    ) => {
      const key =
        adminKey.trim();

      if (!key) {
        setError(
          "Admin API key is required."
        );
        return;
      }

      setOfferSaving(true);
      setError("");
      setMessage("");

      try {
        const response =
          await fetch(
            `${API_BASE}/api/offers/admin`,
            {
              method: "POST",
              headers: {
                "Content-Type":
                  "application/json",
                "X-Admin-API-Key":
                  key,
              },
              body:
                JSON.stringify(
                  offerData
                ),
            }
          );

        const data =
          await response
            .json()
            .catch(
              () => ({})
            );

        if (!response.ok) {
          throw new Error(
            data.message ||
              "Could not publish offer."
          );
        }

        const offerId =
          data.offer?.id ||
          data.offer?._id ||
          data.id ||
          "";

        let flyerUploaded = false;
        let flyerUploadError = "";

        if (flyerFile) {
          if (!offerId) {
            flyerUploadError =
              "The server did not return an offer ID for the flyer upload.";
          } else {
            const imageBody =
              new FormData();

            imageBody.append(
              "image",
              flyerFile
            );

            const imageResponse =
              await fetch(
                `${API_BASE}/api/offers/${encodeURIComponent(
                  offerId
                )}/image`,
                {
                  method: "POST",
                  headers: {
                    "X-Admin-API-Key":
                      key,
                  },
                  body: imageBody,
                }
              );

            const imageData =
              await imageResponse
                .json()
                .catch(
                  () => ({})
                );

            if (!imageResponse.ok) {
              flyerUploadError =
                imageData.message ||
                "The flyer could not be uploaded.";
            } else {
              flyerUploaded = true;
            }
          }
        }

        setOfferModalOpen(
          false
        );
        setOfferTargetBusiness(
          null
        );

        setMessage(
          flyerUploaded
            ? `Offer "${offerData.title}" and flyer were published for ${offerData.business_name}.`
            : flyerUploadError
            ? `Offer "${offerData.title}" was published for ${offerData.business_name}, but the flyer was not uploaded: ${flyerUploadError}`
            : `Offer "${offerData.title}" was published for ${offerData.business_name}.`
        );

        if (
          recordType ===
          "offers"
        ) {
          setActiveStatus(
            "approved"
          );

          await fetchRecords(
            "approved",
            key,
            "offers"
          );
        }
      } catch (err) {
        setError(
          err.message ||
            "Something went wrong while publishing the offer."
        );
      } finally {
        setOfferSaving(
          false
        );
      }
    };

  const deleteBusiness =
    async (
      business
    ) => {
      const key =
        adminKey.trim();

      if (!key) {
        setError(
          "Admin API key is required."
        );
        return;
      }

      const firstConfirmation =
        window.confirm(
          `Permanently delete "${business.name}"?\n\n` +
            "This removes the database record and cannot be undone."
        );

      if (
        !firstConfirmation
      ) {
        return;
      }

      const typedName =
        window.prompt(
          "Type the exact business name to confirm permanent deletion:",
          ""
        );

      if (
        typedName !==
        business.name
      ) {
        setError(
          "Deletion cancelled because the business name did not match."
        );
        return;
      }

      setSavingId(
        business.id
      );

      setError("");
      setMessage("");

      try {
        const response =
          await fetch(
            `${API_BASE}/api/admin/businesses/${encodeURIComponent(
              business.id
            )}`,
            {
              method:
                "DELETE",

              headers: {
                "Content-Type":
                  "application/json",

                "X-Admin-API-Key":
                  key,
              },

              body:
                JSON.stringify(
                  {
                    confirm:
                      "DELETE",
                  }
                ),
            }
          );

        const data =
          await response
            .json()
            .catch(
              () => ({})
            );

        if (!response.ok) {
          throw new Error(
            data.message ||
              "Could not delete business."
          );
        }

        await fetchRecords(
          activeStatus,
          key,
          "businesses"
        );

        setMessage(
          `"${business.name}" was permanently deleted.`
        );
      } catch (err) {
        setError(
          err.message ||
            "Something went wrong while deleting the business."
        );
      } finally {
        setSavingId("");
      }
    };

  const sendEventDecision =
    async (
      eventId,
      decision
    ) => {
      const key =
        adminKey.trim();

      if (!key) {
        throw new Error(
          "Admin API key is required."
        );
      }

      const response =
        await fetch(
          `${API_BASE}/api/admin/events/decision`,
          {
            method:
              "POST",

            headers: {
              "Content-Type":
                "application/json",

              "X-Admin-API-Key":
                key,
            },

            body:
              JSON.stringify(
                {
                  eventId,
                  decision,
                }
              ),
          }
        );

      const data =
        await response
          .json()
          .catch(
            () => ({})
          );

      if (!response.ok) {
        throw new Error(
          data.message ||
            "Could not update event."
        );
      }

      return data;
    };

  const updateEventStatus =
    async (
      event,
      nextStatus
    ) => {
      setSavingId(
        event.id
      );

      setError("");
      setMessage("");

      try {
        await sendEventDecision(
          event.id,
          nextStatus
        );

        await fetchRecords(
          activeStatus,
          adminKey,
          "events"
        );

        setMessage(
          `Event changed to ${nextStatus}.`
        );
      } catch (err) {
        setError(
          err.message ||
            "Something went wrong."
        );
      } finally {
        setSavingId("");
      }
    };

  const deleteEvent =
    async (
      event
    ) => {
      const firstConfirmation =
        window.confirm(
          `Permanently delete "${event.title}"?\n\n` +
            "This removes the event and its flyer images."
        );

      if (
        !firstConfirmation
      ) {
        return;
      }

      const typedTitle =
        window.prompt(
          "Type the exact event title to confirm permanent deletion:",
          ""
        );

      if (
        typedTitle !==
        event.title
      ) {
        setError(
          "Deletion cancelled because the event title did not match."
        );
        return;
      }

      setSavingId(
        event.id
      );

      setError("");
      setMessage("");

      try {
        await sendEventDecision(
          event.id,
          "deleted"
        );

        await fetchRecords(
          activeStatus,
          adminKey,
          "events"
        );

        setMessage(
          `"${event.title}" was permanently deleted.`
        );
      } catch (err) {
        setError(
          err.message ||
            "Something went wrong while deleting the event."
        );
      } finally {
        setSavingId("");
      }
    };

  const uploadExistingOfferFlyer =
    async (
      offer,
      file
    ) => {
      const key =
        adminKey.trim();

      if (!key) {
        setError(
          "Admin API key is required."
        );
        return;
      }

      const allowedTypes =
        new Set([
          "image/jpeg",
          "image/png",
          "image/webp",
        ]);

      if (
        !file ||
        !allowedTypes.has(
          file.type
        )
      ) {
        setError(
          "Offer flyer must be JPG, PNG, or WEBP."
        );
        return;
      }

      if (
        file.size >
        2 * 1024 * 1024
      ) {
        setError(
          "Offer flyer must be 2 MB or smaller."
        );
        return;
      }

      setOfferFlyerUploadingId(
        offer.id
      );
      setError("");
      setMessage("");

      try {
        const body =
          new FormData();

        body.append(
          "image",
          file
        );

        const response =
          await fetch(
            `${API_BASE}/api/offers/${encodeURIComponent(
              offer.id
            )}/image`,
            {
              method: "POST",
              headers: {
                "X-Admin-API-Key":
                  key,
              },
              body,
            }
          );

        const data =
          await response
            .json()
            .catch(
              () => ({})
            );

        if (!response.ok) {
          throw new Error(
            data.message ||
              "Could not upload offer flyer."
          );
        }

        await fetchRecords(
          activeStatus,
          key,
          "offers"
        );

        setMessage(
          offer.image_url
            ? `Flyer replaced for "${offer.title}".`
            : `Flyer added to "${offer.title}".`
        );
      } catch (err) {
        setError(
          err.message ||
            "Something went wrong while uploading the flyer."
        );
      } finally {
        setOfferFlyerUploadingId(
          ""
        );
      }
    };

  const updateOfferStatus =
    async (
      offer,
      decision
    ) => {
      const key =
        adminKey.trim();

      if (!key) {
        setError(
          "Admin API key is required."
        );
        return;
      }

      setSavingId(
        offer.id
      );

      setError("");
      setMessage("");

      try {
        const action =
          decision ===
          "approved"
            ? "approve"
            : "reject";

        const response =
          await fetch(
            `${API_BASE}/api/offers/admin/${encodeURIComponent(
              offer.id
            )}/${action}`,
            {
              method:
                "PATCH",

              headers: {
                "Content-Type":
                  "application/json",

                "X-Admin-API-Key":
                  key,
              },
            }
          );

        const data =
          await response
            .json()
            .catch(
              () => ({})
            );

        if (!response.ok) {
          throw new Error(
            data.message ||
              "Could not update offer."
          );
        }

        await fetchRecords(
          activeStatus,
          key,
          "offers"
        );

        setMessage(
          decision ===
            "approved"
            ? `"${offer.title}" approved and published.`
            : `"${offer.title}" rejected.`
        );
      } catch (err) {
        setError(
          err.message ||
            "Something went wrong while reviewing the offer."
        );
      } finally {
        setSavingId("");
      }
    };

  const deleteOffer =
    async (
      offer
    ) => {
      const key =
        adminKey.trim();

      if (!key) {
        setError(
          "Admin API key is required."
        );
        return;
      }

      const firstConfirmation =
        window.confirm(
          `Permanently delete "${offer.title}"?\n\n` +
            "This removes the offer from GoOurHub and cannot be undone."
        );

      if (
        !firstConfirmation
      ) {
        return;
      }

      const typedTitle =
        window.prompt(
          "Type the exact offer title to confirm permanent deletion:",
          ""
        );

      if (
        typedTitle !==
        offer.title
      ) {
        setError(
          "Deletion cancelled because the offer title did not match."
        );
        return;
      }

      setSavingId(
        offer.id
      );

      setError("");
      setMessage("");

      try {
        const response =
          await fetch(
            `${API_BASE}/api/offers/admin/${encodeURIComponent(
              offer.id
            )}`,
            {
              method:
                "DELETE",

              headers: {
                "X-Admin-API-Key":
                  key,
              },
            }
          );

        const data =
          await response
            .json()
            .catch(
              () => ({})
            );

        if (!response.ok) {
          throw new Error(
            data.message ||
              "Could not delete offer."
          );
        }

        await fetchRecords(
          activeStatus,
          key,
          "offers"
        );

        setMessage(
          `"${offer.title}" was permanently deleted.`
        );
      } catch (err) {
        setError(
          err.message ||
            "Something went wrong while deleting the offer."
        );
      } finally {
        setSavingId("");
      }
    };

  const createOrganization =
    async (
      organizationData
    ) => {
      const key =
        adminKey.trim();

      if (!key) {
        setError(
          "Admin API key is required."
        );
        return;
      }

      setOrganizationSaving(
        true
      );
      setError("");
      setMessage("");

      try {
        const response =
          await fetch(
            `${API_BASE}/api/organizations/admin`,
            {
              method: "POST",
              headers: {
                "Content-Type":
                  "application/json",
                "X-Admin-API-Key":
                  key,
              },
              body:
                JSON.stringify(
                  organizationData
                ),
            }
          );

        const data =
          await response
            .json()
            .catch(
              () => ({})
            );

        if (!response.ok) {
          throw new Error(
            data.message ||
              "Could not add organization."
          );
        }

        setOrganizationFormOpen(
          false
        );

        setMessage(
          `"${organizationData.name}" was added.`
        );

        setActiveStatus(
          data.organization?.status ||
            "pending"
        );

        await fetchRecords(
          data.organization?.status ||
            "pending",
          key,
          "organizations"
        );
      } catch (err) {
        setError(
          err.message ||
            "Something went wrong while adding the organization."
        );
      } finally {
        setOrganizationSaving(
          false
        );
      }
    };

  const updateOrganizationStatus =
    async (
      organization,
      nextStatus
    ) => {
      const key =
        adminKey.trim();

      const id =
        organizationIdOf(
          organization
        );

      if (!key) {
        setError(
          "Admin API key is required."
        );
        return;
      }

      if (!id) {
        setError(
          "Organization ID is missing."
        );
        return;
      }

      setSavingId(id);
      setError("");
      setMessage("");

      try {
        const response =
          await fetch(
            `${API_BASE}/api/organizations/admin/${encodeURIComponent(
              id
            )}/${encodeURIComponent(
              nextStatus
            )}`,
            {
              method: "PATCH",
              headers: {
                "X-Admin-API-Key":
                  key,
              },
            }
          );

        const data =
          await response
            .json()
            .catch(
              () => ({})
            );

        if (!response.ok) {
          throw new Error(
            data.message ||
              "Could not update organization."
          );
        }

        await fetchRecords(
          activeStatus,
          key,
          "organizations"
        );

        setMessage(
          `Organization changed to ${nextStatus}.`
        );
      } catch (err) {
        setError(
          err.message ||
            "Something went wrong while updating the organization."
        );
      } finally {
        setSavingId("");
      }
    };

  const deleteOrganization =
    async (
      organization
    ) => {
      const key =
        adminKey.trim();

      const id =
        organizationIdOf(
          organization
        );

      if (!key) {
        setError(
          "Admin API key is required."
        );
        return;
      }

      if (!id) {
        setError(
          "Organization ID is missing."
        );
        return;
      }

      const confirmation =
        window.confirm(
          `Permanently delete "${organization.name}"?\n\nThis cannot be undone.`
        );

      if (!confirmation) {
        return;
      }

      setSavingId(id);
      setError("");
      setMessage("");

      try {
        const response =
          await fetch(
            `${API_BASE}/api/organizations/admin/${encodeURIComponent(
              id
            )}`,
            {
              method: "DELETE",
              headers: {
                "X-Admin-API-Key":
                  key,
              },
            }
          );

        const data =
          await response
            .json()
            .catch(
              () => ({})
            );

        if (!response.ok) {
          throw new Error(
            data.message ||
              "Could not delete organization."
          );
        }

        await fetchRecords(
          activeStatus,
          key,
          "organizations"
        );

        setMessage(
          `"${organization.name}" was permanently deleted.`
        );
      } catch (err) {
        setError(
          err.message ||
            "Something went wrong while deleting the organization."
        );
      } finally {
        setSavingId("");
      }
    };

  const requestBrowserNotifications =
    async () => {
      if (
        !(
          "Notification" in
          window
        )
      ) {
        setError(
          "Browser notifications are not supported here."
        );
        return;
      }

      const permission =
        await Notification.requestPermission();

      if (
        permission ===
        "granted"
      ) {
        setMessage(
          "Notifications enabled. Keep this admin page open."
        );

        setError("");
      } else {
        setError(
          "Notifications were not enabled."
        );
      }
    };

  useEffect(() => {
    if (
      !adminKey.trim()
    ) {
      return undefined;
    }

    fetchRecords(
      activeStatus,
      adminKey,
      recordType
    );

    const interval =
      window.setInterval(
        () => {
          fetchRecords(
            activeStatus,
            adminKey,
            recordType
          );
        },
        30000
      );

    return () =>
      window.clearInterval(
        interval
      );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeStatus,
    recordType,
  ]);

  useEffect(() => {
    if (
      activeStatus ===
        "pending" &&
      pendingCount > 0 &&
      "Notification" in
        window &&
      Notification.permission ===
        "granted"
    ) {
      new Notification(
        `GoOurHub pending ${noun}`,
        {
          body: `${pendingCount} ${noun}${
            pendingCount === 1
              ? ""
              : "s"
          } need review.`,
        }
      );
    }
  }, [
    activeStatus,
    noun,
    pendingCount,
  ]);

  return (
    <main className="business-admin-page">
      <section className="business-admin-hero">
        <div>
          <span className="business-admin-kicker">
            GoOurHub Admin
          </span>

          <h1>
            Approvals Dashboard
          </h1>

          <p>
            Review businesses,
            community events, offers,
            and nonprofits. Approve
            valid submissions, publish
            local content, and remove
            records when necessary.
          </p>
        </div>

        <button
          type="button"
          className="business-admin-notify-btn"
          onClick={
            requestBrowserNotifications
          }
        >
          Enable notifications
        </button>
      </section>

      <section className="business-admin-key-card">
        <label>
          <span>
            Admin API Key
          </span>

          <input
            type="password"
            value={adminKey}
            onChange={(event) =>
              setAdminKey(
                event.target.value
              )
            }
            placeholder="Paste your admin key"
          />
        </label>

        <div className="business-admin-key-actions">
          <button
            type="button"
            onClick={saveAdminKey}
          >
            Save key
          </button>

          <button
            type="button"
            className="ghost"
            onClick={clearAdminKey}
          >
            Clear
          </button>
        </div>

        <p>
          Do not put this key inside
          public frontend code. Paste
          it here only when managing
          approvals.
        </p>
      </section>
      <AdminConfigManager
        apiBase={API_BASE}
        adminKey={adminKey}
      />
      <section
        className="business-admin-type"
        aria-label="Record type"
      >
        {RECORD_TYPES.map(
          (type) => (
            <button
              key={type}
              type="button"
              className={
                recordType ===
                type
                  ? "is-active"
                  : ""
              }
              onClick={() =>
                switchRecordType(
                  type
                )
              }
            >
              {
                RECORD_TYPE_LABELS[
                  type
                ]
              }
            </button>
          )
        )}
      </section>

      {recordType === "offers" && (
        <section className="business-admin-offer-toolbar">
          <div>
            <strong>Create an offer for any approved business</strong>
            <span>
              Admin-created offers publish immediately.
            </span>
          </div>

          <button
            type="button"
            onClick={() =>
              openOfferCreator()
            }
          >
            + Create offer
          </button>
        </section>
      )}


      {recordType === "organizations" && (
        <>
          <section className="business-admin-org-toolbar">
            <div>
              <strong>Nonprofits & community organizations</strong>
              <span>
                Add organizations, review them, and control what is published.
              </span>
            </div>

            <button
              type="button"
              className="business-admin-add-btn"
              onClick={() =>
                setOrganizationFormOpen(
                  (open) => !open
                )
              }
            >
              {organizationFormOpen
                ? "Close form"
                : "+ Add nonprofit"}
            </button>
          </section>

          {organizationFormOpen && (
            <OrganizationForm
              saving={
                organizationSaving
              }
              onCancel={() =>
                setOrganizationFormOpen(
                  false
                )
              }
              onSubmit={
                createOrganization
              }
            />
          )}
        </>
      )}

      <section className="business-admin-tabs">
        {STATUS_TABS.map(
          (status) => (
            <button
              key={status}
              type="button"
              className={
                activeStatus ===
                status
                  ? "is-active"
                  : ""
              }
              onClick={() =>
                setActiveStatus(
                  status
                )
              }
            >
              {status}
            </button>
          )
        )}

        <button
          type="button"
          className="business-admin-refresh"
          onClick={() =>
            fetchRecords(
              activeStatus,
              adminKey,
              recordType
            )
          }
        >
          Refresh
        </button>
      </section>

      <section className="business-admin-search">
        <div className="business-admin-search-box">
          <span
            className="business-admin-search-icon"
            aria-hidden="true"
          >
            🔍
          </span>

          <input
            type="search"
            value={searchTerm}
            onChange={(event) =>
              setSearchTerm(
                event.target.value
              )
            }
            placeholder={
              recordType ===
              "events"
                ? "Search by title, community, venue, organizer…"
                : recordType ===
                  "offers"
                ? "Search by business, offer title, promo phrase…"
                : recordType ===
                  "organizations"
                ? "Search nonprofit name, community, mission, website…"
                : "Search by name, phone, city, category, ID…"
            }
            aria-label={`Search ${recordType}`}
          />

          {searchTerm && (
            <button
              type="button"
              className="business-admin-search-clear"
              onClick={() =>
                setSearchTerm(
                  ""
                )
              }
            >
              Clear
            </button>
          )}
        </div>

        {searchTerm.trim() &&
          !loading && (
            <p className="business-admin-search-count">
              Showing{" "}
              {
                filteredRecords.length
              }{" "}
              of {records.length}{" "}
              {activeStatus}{" "}
              {noun}
              {records.length ===
              1
                ? ""
                : "s"}
            </p>
          )}
      </section>

      {message && (
        <div className="business-admin-alert success">
          {message}
        </div>
      )}

      {error && (
        <div className="business-admin-alert error">
          {error}
        </div>
      )}

      <section className="business-admin-list">
        {loading ? (
          <div className="business-admin-empty">
            Loading{" "}
            {recordType}...
          </div>
        ) : records.length ===
          0 ? (
          <div className="business-admin-empty">
            No {activeStatus}{" "}
            {recordType} right now.
          </div>
        ) : filteredRecords.length ===
          0 ? (
          <div className="business-admin-empty">
            No {activeStatus}{" "}
            {recordType} match
            &quot;
            {searchTerm.trim()}
            &quot;.
          </div>
        ) : recordType ===
          "businesses" ? (
          filteredRecords.map(
            (business) => (
              <BusinessCard
                key={
                  business.id
                }
                business={
                  business
                }
                savingId={
                  savingId
                }
                photoUploadingId={
                  photoUploadingId
                }
                onStatusChange={
                  updateBusinessStatus
                }
                onDelete={
                  deleteBusiness
                }
                onUploadPhotos={
                  uploadBusinessPhotos
                }
                onCreateOffer={
                  openOfferCreator
                }
              />
            )
          )
        ) : recordType ===
          "offers" ? (
          filteredRecords.map(
            (offer) => (
              <OfferCard
                key={offer.id}
                offer={offer}
                savingId={
                  savingId
                }
                flyerUploadingId={
                  offerFlyerUploadingId
                }
                onDecision={
                  updateOfferStatus
                }
                onDelete={
                  deleteOffer
                }
                onUploadFlyer={
                  uploadExistingOfferFlyer
                }
              />
            )
          )
        ) : recordType ===
          "organizations" ? (
          filteredRecords.map(
            (
              organization,
              index
            ) => (
              <OrganizationCard
                key={
                  organizationIdOf(
                    organization
                  ) ||
                  organization.slug ||
                  `${organization.name}-${index}`
                }
                organization={
                  organization
                }
                savingId={
                  savingId
                }
                onStatusChange={
                  updateOrganizationStatus
                }
                onDelete={
                  deleteOrganization
                }
              />
            )
          )
        ) : (
          filteredRecords.map(
            (event) => (
              <EventCard
                key={event.id}
                event={event}
                savingId={
                  savingId
                }
                onStatusChange={
                  updateEventStatus
                }
                onDelete={
                  deleteEvent
                }
              />
            )
          )
        )}
      </section>

      {offerModalOpen && (
        <OfferCreateModal
          businesses={
            offerBusinesses
          }
          initialBusiness={
            offerTargetBusiness
          }
          loadingBusinesses={
            offerBusinessesLoading
          }
          saving={
            offerSaving
          }
          onClose={() => {
            if (
              !offerSaving
            ) {
              setOfferModalOpen(
                false
              );
              setOfferTargetBusiness(
                null
              );
            }
          }}
          onSubmit={
            createAdminOffer
          }
        />
      )}
    </main>
  );
}